import { useState, useCallback } from 'react';
import { endOfDay, startOfDay, startOfYear, subDays } from 'date-fns';
import { DateFilterState, DatePreset } from '../types/metrics-type';

const getPresetDates = (
  preset: Exclude<DatePreset, 'custom'>,
): { startDate: Date; endDate: Date } => {
  const now = new Date();
  const endDate = endOfDay(now);

  switch (preset) {
    case 'allTime': {
      return { startDate: new Date(0), endDate };
    }
    case 'last7days': {
      return { startDate: startOfDay(subDays(now, 6)), endDate };
    }
    case 'last30days': {
      return { startDate: startOfDay(subDays(now, 29)), endDate };
    }
    case 'last90days': {
      return { startDate: startOfDay(subDays(now, 89)), endDate };
    }
    case 'thisYear': {
      return { startDate: startOfYear(now), endDate };
    }
    default:
      return { startDate: new Date(now), endDate: new Date(now) };
  }
};

export function useDateFilter(initialPreset: DatePreset = 'allTime') {
  const safeInitialPreset = initialPreset === 'custom' ? 'allTime' : initialPreset;
  const initialDates = getPresetDates(safeInitialPreset);

  const [dateFilter, setDateFilter] = useState<DateFilterState>({
    preset: initialPreset,
    ...initialDates,
  });

  const setPreset = useCallback((preset: DatePreset) => {
    const dates = getPresetDates(preset === 'custom' ? 'allTime' : preset);
    setDateFilter({
      preset,
      ...dates,
    });
  }, []);

  const setCustomDates = useCallback((startDate: Date, endDate: Date) => {
    setDateFilter({
      preset: 'custom',
      startDate,
      endDate,
    });
  }, []);

  const resetFilter = useCallback(() => {
    const dates = getPresetDates('allTime');
    setDateFilter({
      preset: 'allTime',
      ...dates,
    });
  }, []);

  return {
    dateFilter,
    setPreset,
    setCustomDates,
    lastWeekDates: getPresetDates('last7days'),
    lastMonthDates: getPresetDates('last30days'),
    resetFilter,
  };
}
